

import { ProjectData, PromptTemplate, DEFAULT_PROMPTS, ProjectStatus, Inspiration } from '../types';

// API Endpoints
const API_BASE = '/api';
const DB_NAME = 'LVA_DB';
const DB_VERSION = 2; // Increased for 'tools' store
const STORE_PROJECTS = 'projects';
const STORE_INSPIRATIONS = 'inspirations';
const STORE_TOOLS = 'tools'; // New store
const KEY_PROMPTS = 'lva_prompts'; 
const KEY_LAST_UPLOAD = 'lva_last_upload_time';
const KEY_LAST_LOCAL_CHANGE = 'lva_last_local_change_time';
const KEY_PENDING_CHANGES = 'lva_pending_changes';
const KEY_R2_DOMAIN = 'lva_r2_domain'; // New key for R2 domain

// --- IndexedDB Helpers ---

const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_PROJECTS)) {
        db.createObjectStore(STORE_PROJECTS, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORE_INSPIRATIONS)) {
        db.createObjectStore(STORE_INSPIRATIONS, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORE_TOOLS)) {
        db.createObjectStore(STORE_TOOLS, { keyPath: 'id' });
      }
    };

    request.onsuccess = (event) => {
      resolve((event.target as IDBOpenDBRequest).result);
    };

    request.onerror = (event) => {
      reject((event.target as IDBOpenDBRequest).error);
    };
  });
};

const dbGetAll = async <T>(storeName: string): Promise<T[]> => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result as T[]);
    request.onerror = () => reject(request.error);
  });
};

const dbGet = async <T>(storeName: string, key: string): Promise<T | undefined> => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readonly');
    const store = transaction.objectStore(storeName);
    const request = store.get(key);
    request.onsuccess = () => resolve(request.result as T);
    request.onerror = () => reject(request.error);
  });
};

const dbPut = async <T>(storeName: string, value: T): Promise<void> => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.put(value);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

const dbDelete = async (storeName: string, key: string): Promise<void> => {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, 'readwrite');
    const store = transaction.objectStore(storeName);
    const request = store.delete(key);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
};

// --- Mutex for Atomic Updates ---
class Mutex {
  private queue: Promise<void> = Promise.resolve();

  async lock<T>(task: () => Promise<T>): Promise<T> {
    let release: () => void;
    const currentLock = new Promise<void>(resolve => { release = resolve; });
    
    // Wait for previous task
    const previous = this.queue;
    this.queue = this.queue.then(() => currentLock);

    await previous;
    
    try {
      return await task();
    } finally {
      release!();
    }
  }
}

const projectMutex = new Mutex();

// --- Manual Sync Methods (D1 Bulk) ---

export const getPendingChanges = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem(KEY_PENDING_CHANGES) || '[]');
  } catch { return []; }
};

export const trackChange = (source: string = 'unknown') => {
  localStorage.setItem(KEY_LAST_LOCAL_CHANGE, Date.now().toString());
  
  const pending = new Set(getPendingChanges());
  pending.add(source);
  localStorage.setItem(KEY_PENDING_CHANGES, JSON.stringify(Array.from(pending)));
};

const clearPendingChange = (source: string) => {
  const pending = new Set(getPendingChanges());
  pending.delete(source);
  localStorage.setItem(KEY_PENDING_CHANGES, JSON.stringify(Array.from(pending)));
};

export const hasUnsavedChanges = (): boolean => {
  const pending = getPendingChanges();
  return pending.length > 0;
};

export const getLastUploadTime = (): string => {
  const ts = localStorage.getItem(KEY_LAST_UPLOAD);
  if (!ts) return '从未上传';
  const date = new Date(parseInt(ts));
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日 / ${pad(date.getHours())}：${pad(date.getMinutes())}：${pad(date.getSeconds())}`;
};

export const updateLastUploadTime = () => {
  localStorage.setItem(KEY_LAST_UPLOAD, Date.now().toString());
};

// Granular Upload Functions for Progress Tracking

export const uploadProjects = async (): Promise<void> => {
  const projects = await dbGetAll<ProjectData>(STORE_PROJECTS);
  
  // SANITIZATION: Remove Base64 images from payload
  // We only want to sync metadata and cloud URLs to D1.
  const sanitizedProjects = projects.map(p => {
    const copy = { ...p };
    
    if (copy.storyboard) {
        copy.storyboard = copy.storyboard.map(frame => ({
            ...frame,
            // If it's a base64 string (starts with data:), don't send it to server.
            // If it's a URL (starts with /api/ or http), keep it.
            imageUrl: frame.imageUrl?.startsWith('data:') ? undefined : frame.imageUrl
        }));
    }
    
    if (copy.coverImage?.imageUrl?.startsWith('data:')) {
        copy.coverImage = { ...copy.coverImage, imageUrl: '' };
    }

    return copy;
  });

  const payload = { projects: sanitizedProjects };
  
  const res = await fetch(`${API_BASE}/sync`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.error || `Projects upload failed: ${res.statusText}`);
  }
  
  clearPendingChange('projects');
};

export const uploadInspirations = async (): Promise<void> => {
  const inspirations = await dbGetAll<Inspiration>(STORE_INSPIRATIONS);
  const payload = { inspirations };
  
  const res = await fetch(`${API_BASE}/sync`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.error || `Inspirations upload failed: ${res.statusText}`);
  }
  
  clearPendingChange('inspirations');
};

export const uploadPrompts = async (): Promise<void> => {
  const promptsStr = localStorage.getItem(KEY_PROMPTS);
  const prompts = promptsStr ? JSON.parse(promptsStr) : DEFAULT_PROMPTS;
  const payload = { prompts };
  
  const res = await fetch(`${API_BASE}/sync`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.error || `Settings upload failed: ${res.statusText}`);
  }
  
  clearPendingChange('settings');
};

export const uploadTools = async (): Promise<void> => {
  const tools = await dbGetAll<{id: string, data: any}>(STORE_TOOLS);
  const payload = { tools };
  
  const res = await fetch(`${API_BASE}/sync`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload)
  });

  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.error || `Tools upload failed: ${res.statusText}`);
  }
  
  clearPendingChange('tools');
};

export const downloadAllData = async (): Promise<void> => {
  // 1. Fetch from D1 Sync Endpoint
  const res = await fetch(`${API_BASE}/sync`);
  if (!res.ok) {
    const errData = await res.json().catch(() => ({}));
    throw new Error(errData.error || `Download failed: ${res.statusText}`);
  }

  const data = await res.json();

  // 2. Update Local Stores (Merge/Overwrite)
  if (data.projects && Array.isArray(data.projects)) {
    for (const p of data.projects) await dbPut(STORE_PROJECTS, p);
  }
  
  if (data.inspirations && Array.isArray(data.inspirations)) {
    for (const i of data.inspirations) await dbPut(STORE_INSPIRATIONS, i);
  }

  if (data.tools && Array.isArray(data.tools)) {
    for (const t of data.tools) await dbPut(STORE_TOOLS, t);
  }
  
  if (data.prompts) {
    const merged = { ...DEFAULT_PROMPTS, ...data.prompts };
    localStorage.setItem(KEY_PROMPTS, JSON.stringify(merged));
  }
  
  // Update last upload time to match download (synced state)
  updateLastUploadTime();
  // Also sync the local change time so it doesn't show as dirty immediately
  localStorage.setItem(KEY_LAST_LOCAL_CHANGE, Date.now().toString());
  localStorage.setItem(KEY_PENDING_CHANGES, '[]');
};

// New Method: Fetch and update a SINGLE project from cloud (Lightweight)
export const syncProject = async (id: string): Promise<ProjectData | null> => {
  try {
      // Fetch specifically just one project to save bandwidth/time
      const res = await fetch(`${API_BASE}/projects/${id}`);
      
      if (res.status === 404) {
          // Project might be deleted on cloud, do nothing or handle
          return null; 
      }
      
      if (!res.ok) throw new Error('Failed to fetch project');
      
      const project = await res.json();
      
      // Update Local DB
      await dbPut(STORE_PROJECTS, project);
      
      return project as ProjectData;
  } catch (e) {
      console.warn("Single project sync failed", e);
      return null;
  }
};

// --- Image Operations (R2) ---

export const getR2PublicDomain = () => localStorage.getItem(KEY_R2_DOMAIN) || '';
export const setR2PublicDomain = (domain: string) => localStorage.setItem(KEY_R2_DOMAIN, domain);

/**
 * Resolves an image URL to a Public CDN URL if configured, 
 * bypassing the slow /api/images proxy.
 */
export const resolveImageUrl = (url?: string) => {
  if (!url) return '';
  if (url.startsWith('data:')) return url;
  if (url.startsWith('http')) return url;
  
  const domain = getR2PublicDomain();
  if (!domain) return url; // Fallback to proxy if no domain configured
  
  // Pattern: /api/images/encoded_key
  const match = url.match(/\/api\/images\/(.+)$/);
  if (match && match[1]) {
    const key = decodeURIComponent(match[1]); // e.g. "projectId/uuid.png"
    // Normalize domain (remove trailing slash)
    const normalizedDomain = domain.replace(/\/$/, '');
    // Key usually doesn't start with slash because of how we store it, but safe to check
    const normalizedKey = key.startsWith('/') ? key.substring(1) : key;
    return `${normalizedDomain}/${normalizedKey}`;
  }
  
  return url;
};

export const uploadImage = async (base64: string, projectId?: string): Promise<string> => {
  // Convert base64 to blob
  const byteString = atob(base64.split(',')[1]);
  const mimeString = base64.split(',')[0].split(':')[1].split(';')[0];
  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }
  const blob = new Blob([ab], { type: mimeString });

  const ext = mimeString.split('/')[1] || 'png';
  const filename = `${crypto.randomUUID()}.${ext}`;

  // Upload to R2 Endpoint with optional project folder param
  const url = new URL(`${window.location.origin}${API_BASE}/images/${filename}`);
  if (projectId) {
      url.searchParams.set('project', projectId);
  }

  const res = await fetch(url.toString(), {
    method: 'PUT',
    body: blob
  });

  if (!res.ok) {
    throw new Error('Image upload failed');
  }
  
  const data = await res.json();
  return data.url; // e.g. /api/images/encodedPath
};

export const deleteImage = async (imageUrl: string): Promise<void> => {
  // Extract key from URL: /api/images/[encodedKey]
  const match = imageUrl.match(/\/api\/images\/(.+)$/);
  if (match && match[1]) {
    try {
      await fetch(`${API_BASE}/images/${match[1]}`, { method: 'DELETE' });
    } catch (e) {
      console.warn("Failed to delete image from R2", e);
    }
  }
};

// --- Local CRUD Methods (No Network) ---

export const getProjects = async (): Promise<ProjectData[]> => {
  try {
    return await dbGetAll<ProjectData>(STORE_PROJECTS);
  } catch (error) {
    console.error("DB Error", error);
    return [];
  }
};

export const getProject = async (id: string): Promise<ProjectData | undefined> => {
  try {
    return await dbGet<ProjectData>(STORE_PROJECTS, id);
  } catch (e) { 
    return undefined;
  }
};

export const saveProject = async (project: ProjectData): Promise<void> => {
  const payload = {
    ...project,
    updatedAt: Date.now()
  };
  await dbPut(STORE_PROJECTS, payload);
  trackChange('projects');
};

const checkProjectCompletion = (p: ProjectData): boolean => {
  if (!p) return false;
  // Criteria: Script, Titles, Storyboard (Text), Summary, Cover, and at least 1 generated Image
  const hasScript = !!p.script && p.script.length > 0;
  const hasTitles = !!p.titles && p.titles.length > 0;
  const hasSbText = !!p.storyboard && p.storyboard.length > 0;
  const hasSummary = !!p.summary && p.summary.length > 0;
  const hasCover = !!p.coverOptions && p.coverOptions.length > 0;
  const hasImages = p.storyboard?.some(f => !!f.imageUrl) || false;

  return hasScript && hasTitles && hasSbText && hasSummary && hasCover && hasImages;
};

export const updateProject = async (id: string, updater: (current: ProjectData) => ProjectData): Promise<ProjectData | null> => {
  return projectMutex.lock(async () => {
      const current = await dbGet<ProjectData>(STORE_PROJECTS, id);
      if (!current) return null;

      const updated = updater(current);
      
      // Auto-status update based on content completeness
      if (updated.status !== ProjectStatus.ARCHIVED) {
          if (checkProjectCompletion(updated)) {
              updated.status = ProjectStatus.COMPLETED;
          } else if (updated.status === ProjectStatus.COMPLETED && !checkProjectCompletion(updated)) {
              // Downgrade to IN_PROGRESS if data was removed
              updated.status = ProjectStatus.IN_PROGRESS;
          } else if (updated.script && updated.status === ProjectStatus.DRAFT) {
              // Upgrade to IN_PROGRESS if script exists
              updated.status = ProjectStatus.IN_PROGRESS;
          }
      }

      updated.updatedAt = Date.now();
      
      await dbPut(STORE_PROJECTS, updated);
      trackChange('projects');
      return updated;
  });
};

export const archiveProject = async (id: string): Promise<void> => {
    await updateProject(id, (p) => ({ ...p, status: ProjectStatus.ARCHIVED }));
};

export const unarchiveProject = async (id: string): Promise<void> => {
    await updateProject(id, (p) => ({ 
        ...p, 
        // Logic in updateProject will automatically verify if it's COMPLETED or IN_PROGRESS
        status: ProjectStatus.IN_PROGRESS 
    }));
};

export const createProject = async (initialTitle?: string): Promise<string> => {
  const newProject: ProjectData = {
    id: crypto.randomUUID(),
    title: initialTitle || '未命名项目',
    status: ProjectStatus.DRAFT,
    createdAt: Date.now(),
    updatedAt: Date.now(),
    inputs: {
      topic: initialTitle || '',
      tone: '信息丰富且引人入胜',
      language: '中文'
    }
  };
  
  await saveProject(newProject);
  return newProject.id;
};

export const deleteProject = async (id: string): Promise<void> => {
  // 1. Get project to find images
  const project = await dbGet<ProjectData>(STORE_PROJECTS, id);
  
  if (project) {
    // 2. Delete cloud images from R2
    const imagesToDelete: string[] = [];
    
    // Collect storyboard images
    if (project.storyboard) {
      project.storyboard.forEach(f => {
        if (f.imageUrl && f.imageUrl.includes('/api/images/')) {
          imagesToDelete.push(f.imageUrl);
        }
      });
    }
    
    // Collect cover image
    if (project.coverImage?.imageUrl && project.coverImage.imageUrl.includes('/api/images/')) {
      imagesToDelete.push(project.coverImage.imageUrl);
    }

    // Execute deletes in background
    for (const imgUrl of imagesToDelete) {
      await deleteImage(imgUrl);
    }
  }

  // 3. Delete from Local DB
  await dbDelete(STORE_PROJECTS, id);
  
  // 4. Delete from Remote D1
  try {
    await fetch(`${API_BASE}/projects/${id}`, { method: 'DELETE' });
  } catch (e) {
    console.warn("Failed to delete project from D1", e);
  }

  trackChange('projects');
};

// --- Prompts (Local Only) ---

export const getPrompts = async (): Promise<Record<string, PromptTemplate>> => {
  try {
    const local = localStorage.getItem(KEY_PROMPTS);
    if (local) {
      return { ...DEFAULT_PROMPTS, ...JSON.parse(local) };
    }
  } catch (e) { /* ignore */ }
  return DEFAULT_PROMPTS;
};

export const savePrompts = async (prompts: Record<string, PromptTemplate>): Promise<void> => {
  localStorage.setItem(KEY_PROMPTS, JSON.stringify(prompts));
  trackChange('settings');
};

export const resetPrompts = async (): Promise<void> => {
  await savePrompts(DEFAULT_PROMPTS);
};

// --- Inspiration Methods (Local Only) ---

export const getInspirations = async (): Promise<Inspiration[]> => {
  try {
    return await dbGetAll<Inspiration>(STORE_INSPIRATIONS);
  } catch (e) {
    return [];
  }
};

export const saveInspiration = async (item: Inspiration): Promise<void> => {
  await dbPut(STORE_INSPIRATIONS, item);
  trackChange('inspirations');
};

export const deleteInspiration = async (id: string): Promise<void> => {
  await dbDelete(STORE_INSPIRATIONS, id);
  try {
    await fetch(`${API_BASE}/inspirations/${id}`, { method: 'DELETE' });
  } catch (e) {}
  trackChange('inspirations');
};

// --- Tools Methods (Real-time Cloud Sync) ---

export const saveToolData = async (id: string, data: any): Promise<void> => {
    // 1. Save Local
    await dbPut(STORE_TOOLS, { id, data });
    trackChange('tools');
};

export const uploadToolData = async (id: string, data: any): Promise<void> => {
    // 2. Push to Cloud (Individual Update)
    const payload = {
        tools: [{ id, data }]
    };
    
    const res = await fetch(`${API_BASE}/sync`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
    });

    if (!res.ok) {
        throw new Error('Cloud save failed');
    }
    clearPendingChange('tools');
};

export const fetchRemoteToolData = async <T>(id: string): Promise<T | null> => {
    try {
        const res = await fetch(`${API_BASE}/tools/${id}`);
        if (!res.ok) return null;
        const data = await res.json();
        return data as T;
    } catch {
        return null;
    }
};

export const getToolData = async <T>(id: string): Promise<T | null> => {
    try {
        const item = await dbGet<{id: string, data: T}>(STORE_TOOLS, id);
        return item ? item.data : null;
    } catch { 
        return null;
    }
};
